import { useEffect, useImperativeHandle, useRef, useState, type Ref } from "react";
import { inspectFoodOptions, updateFoodOption, type FoodOptionGroup, type ParsedVoiceMealDraft } from "../../application/remote-meal-parser";
import type { VoiceTranscriptionSession } from "../../application/voice-transcription";
import { mergeVoiceTranscript } from "../../application/voice-transcript";
import { webSpeechTranscription } from "../../voice/web-speech-transcription";
import { isNutritionRowComplete } from "../../application/nutrition-completeness";

export type FoodSettingsHandle = { prepareSave: () => Promise<ParsedVoiceMealDraft | null> };

export function FoodSettings({ draft, index, onChange, onBusy, ref }: { draft: ParsedVoiceMealDraft; index: number; onChange: (draft: ParsedVoiceMealDraft) => void; onBusy: (busy: boolean) => void; ref?: Ref<FoodSettingsHandle> }) {
  const row = draft.nutrition!.rows[index]!;
  const basisUnit = row.portion?.includes("мл") ? "мл" : "г";
  const [caloriesOpen, setCaloriesOpen] = useState(false);
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [voicePending, setVoicePending] = useState(false);
  const voiceBusy = listening || voicePending;
  const [inspected, setInspected] = useState<{ options: FoodOptionGroup[]; help?: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const session = useRef<VoiceTranscriptionSession | null>(null);
  const speech = useRef("");
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; session.current?.abort(); }; }, []);
  useEffect(() => { onBusy(busy || voiceBusy); return () => onBusy(false); }, [busy, voiceBusy, onBusy]);
  useEffect(() => {
    let active = true;
    setInspected(null); setLoading(true);
    inspectFoodOptions(draft, index).then(result => { if (active) setInspected(result); })
      .catch(() => { if (active) setError("Не удалось обновить варианты. Закройте и откройте их ещё раз."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [draft, index]);
  const options = inspected?.options ?? row.options ?? [];
  const help = inspected?.help ?? row.help;
  const renderGroup = (option: FoodOptionGroup) => <fieldset key={option.key} disabled={busy || voiceBusy || loading}>
    <legend>{option.label}</legend>
    <div className="food-setting-choices">{option.choices.map(choice => <button key={choice.value} type="button" aria-pressed={option.selected === choice.value} onClick={() => void apply(option.key, choice.value)}><span className="food-setting-label">{choice.label}</span>{choice.protein100 != null && <small>Белок {choice.protein100} г / 100 {basisUnit}</small>}{choice.kcal100 != null && <small>≈ {choice.kcal100} ккал / 100 {basisUnit}</small>}</button>)}</div>
  </fieldset>;

  const apply = async (key: string, selected: string | number, transcript?: string) => {
    if (busy || voiceBusy) return null;
    setError(""); setBusy(true);
    try {
      const updated = await updateFoodOption(draft, index, key, selected, transcript);
      if (alive.current) { onChange(updated); if (key === "kcal100") { setCaloriesOpen(false); setValue(""); } return updated; }
      return null;
    } catch (problem) { if (alive.current) setError(problem instanceof Error ? problem.message : "Не удалось сохранить."); return null; }
    finally { if (alive.current) setBusy(false); }
  };
  useImperativeHandle(ref, () => ({ prepareSave: async () => value.trim() ? apply("kcal100", 0, value) : draft }));
  const microphone = () => {
    if (session.current) { session.current.stop(); return; }
    setError(""); speech.current = ""; setVoicePending(true);
    session.current = webSpeechTranscription.start("ru-RU", {
      onListening: () => { setVoicePending(false); setListening(true); },
      onCaptureEnd: () => { setListening(false); setVoicePending(true); },
      onInterim: text => setValue(mergeVoiceTranscript(speech.current, text)),
      onFinal: text => { speech.current = mergeVoiceTranscript(speech.current, text); setValue(speech.current); },
      onError: () => { session.current = null; setVoicePending(false); setListening(false); setError("Не удалось распознать речь. Введите число вручную."); },
      onEnd: () => { setVoicePending(false); setListening(false); session.current = null; },
    });
  };
  return <section className="food-settings" aria-label="Настройки продукта">
    {row.assumption && <p className="food-settings-note">{row.assumption}</p>}
    {!isNutritionRowComplete(row) && <button className="food-calorie-toggle" type="button" disabled={busy || voiceBusy} onClick={() => void apply("recalculate", 0)}>Найти аналоги и пересчитать</button>}
    {!row.profileKey && !!options.length && <p className="food-settings-note">Выбор применит новую типовую оценку к этой порции.</p>}
    {loading && <p role="status">Загружаю варианты…</p>}
    {options.slice(0,2).map(renderGroup)}
    {options.length > 2 && <details><summary>Дополнительные характеристики</summary>{options.slice(2).map(renderGroup)}</details>}
    {!!options.length && help && <details className="food-settings-help"><summary>Как мы считаем?</summary><p>{help}</p></details>}
    <button className="food-calorie-toggle" type="button" disabled={busy || voiceBusy} onClick={() => setCaloriesOpen(open => !open)}>Указать / продиктовать ккал на 100 {basisUnit}</button>
    {caloriesOpen && <div className="food-calorie-entry"><label>Калорийность на упаковке, ккал / 100 {basisUnit}<input aria-label="Ккал на 100 грамм" inputMode="decimal" value={value} disabled={busy || voiceBusy} onChange={event => setValue(event.target.value)} placeholder="Например, 250" /></label><div><button type="button" disabled={busy || voicePending} onClick={microphone}>{voicePending ? "Подготавливаю…" : listening ? "Закончить запись" : "Продиктовать"}</button><button type="button" disabled={busy || voiceBusy || !value.trim()} onClick={() => void apply("kcal100", 0, value)}>Применить</button></div><small>Меняет калории этой записи. БЖУ останутся приблизительными, если вы их не сообщили.</small></div>}
    {busy && <p role="status">Пересчитываю…</p>}
    {error && <p role="alert">{error}</p>}
  </section>;
}
